import React, { useEffect, useRef, useState } from "react";

import { CheckOutlined } from "@ant-design/icons";
import { Card, Form, Input, Button } from "antd";
import Parse from "parse";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import logoDark from "../assets/nomyx_logo_dark.png";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // "email" | "verify" | "reset"
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [codeError, setCodeError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [password, setPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const inputRefs = useRef([]);
  const [resetForm] = Form.useForm();

  // A stale/revoked Parse session (left behind by logout, which doesn't clear
  // the Parse SDK's own local cache)
  useEffect(() => {
    if (Parse.User.current()) {
      Parse.User.logOut().catch(() => {});
    }
  }, []);

  const passwordCriteria = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const renderIcon = (condition) =>
    condition ? <CheckOutlined className="!text-green-500" /> : <span className="w-2 h-2 rounded-full bg-gray-500 inline-block" />;

  // Step 1: request code
  const handleEmailSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await Parse.Cloud.run("requestPasswordReset", { email: values.email });
      if (response.success) {
        toast.success(`We have sent a verification code to ${values.email}.`);
        setEmail(values.email.toLowerCase().trim());
        setStep("verify");
      } else {
        toast.error("Failed to send password reset email!");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to send password reset email!");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: OTP input handlers
  const handleCodeChange = (index, value) => {
    const sanitized = value
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase()
      .slice(0, 1);
    const newCode = [...code];
    newCode[index] = sanitized;
    setCode(newCode);
    setCodeError("");
    if (sanitized && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase()
      .slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(""));
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const fullCode = code.join("");
    if (fullCode.length < 6) {
      setCodeError("Please enter the complete 6-character code.");
      return;
    }
    setVerifying(true);
    try {
      const response = await Parse.Cloud.run("verifyPasswordResetCode", { email, code: fullCode });
      if (response.success) {
        setStep("reset");
      } else {
        setCodeError(response.message || "Verification failed. Please try again.");
      }
    } catch (err) {
      setCodeError(err.message || "Invalid or expired code. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setCode(["", "", "", "", "", ""]);
    setCodeError("");
    inputRefs.current[0]?.focus();
    try {
      await toast.promise(Parse.Cloud.run("resendPasswordResetCode", { email }), {
        pending: "Resending code...",
        success: "Verification code resent!",
        error: "Failed to resend code.",
      });
    } finally {
      setResending(false);
    }
  };

  // Step 3: reset password
  const handleResetSubmit = async (values) => {
    const isPasswordValid = Object.values(passwordCriteria).every(Boolean);
    if (!isPasswordValid) {
      toast.error("Please meet all password requirements.");
      return;
    }

    setResetting(true);
    try {
      const response = await Parse.Cloud.run("resetPassword", {
        token: code.join(""),
        newPassword: values.password,
      });

      if (response.success) {
        toast.success(response.message || "Password has been reset successfully!");
        navigate("/login");
      } else {
        toast.error(response.message || "Failed to reset password.");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to reset password.");
    } finally {
      setResetting(false);
    }
  };

  const cardTitle = step === "email" ? "Forgot Password" : step === "verify" ? "Enter Verification Code" : "Reset Password";

  return (
    <div
      className="relative w-full min-h-screen overflow-hidden flex flex-col"
      style={{
        backgroundImage: "url('/images/nomyx_banner.svg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <h1 className="text-right font-bold text-xl mb-4 w-full mt-8 !-ml-10 text-white">NomyxID</h1>
      <div className="flex flex-1 flex-col lg:flex-row">
        {/* Left Side */}
        <div className="w-full lg:w-1/2 flex flex-col items-center justify-center px-4 md:px-6 my-10">
          <div className="w-full max-w-2xl">
            <img src={logoDark} alt="Logo" width={630} height={240} priority="true" />
          </div>
        </div>

        {/* Right Side */}
        <div className="max-[550px]:hidden w-1/2 flex flex-col justify-center items-center p-2">
          <div className="flex-grow flex items-center justify-center w-full">
            <div className="bg-nomyxDark1 bg-opacity-90 text-nomyxWhite shadow-lg rounded-lg p-4 max-w-2xl w-full">
              <div className="w-full flex flex-col justify-center items-center">
                <Card
                  title={<span className="text-white">{cardTitle}</span>}
                  style={{ width: "100%", maxWidth: "550px", border: "none" }}
                  className="signup-card bg-transparent shadow-lg rounded-lg wallet-setup-radio-group"
                >
                  {/* Step 1: Email */}
                  {step === "email" && (
                    <>
                      <p className="mb-6 text-left !text-white font-normal text-sm">
                        Enter your email, and we will send you a verification code to reset your password.
                      </p>
                      <Form layout="vertical" onFinish={handleEmailSubmit} className="w-full">
                        <Form.Item
                          name="email"
                          label={<span className="text-gray-400">Email</span>}
                          rules={[
                            { required: true, message: "Please input your email!" },
                            { type: "email", message: "Please enter a valid email address!" },
                          ]}
                        >
                          <Input
                            type="email"
                            placeholder="Please enter your email"
                            className="signup-input placeholder-nomyxGray1 !border-nomyxGray1"
                          />
                        </Form.Item>

                        <Form.Item>
                          <div className="flex justify-between items-center">
                            <Link to="/login" className="font-semibold text-blue-400">
                              Back to Login
                            </Link>
                            <Button type="primary" htmlType="submit" className="signup-button" loading={loading}>
                              Submit
                            </Button>
                          </div>
                        </Form.Item>
                      </Form>
                    </>
                  )}

                  {/* Step 2: Verify OTP */}
                  {step === "verify" && (
                    <>
                      <p className="text-gray-400 text-sm mb-6">
                        We sent a 6-character code to <b className="text-white">{email}</b>. Enter it below to continue.
                      </p>

                      <div className="flex gap-3 justify-center mb-4">
                        {code.map((char, index) => (
                          <input
                            key={index}
                            ref={(el) => {
                              inputRefs.current[index] = el;
                            }}
                            type="text"
                            inputMode="text"
                            maxLength={1}
                            value={char}
                            onChange={(e) => handleCodeChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={handlePaste}
                            className="w-12 h-14 text-center text-xl font-bold uppercase rounded-md border-2 border-gray-600 bg-transparent text-white focus:border-blue-500 focus:outline-none transition-colors"
                            style={{ caretColor: "white" }}
                          />
                        ))}
                      </div>

                      {codeError && <p className="text-red-500 text-sm text-center mb-4">{codeError}</p>}

                      <Button
                        type="primary"
                        onClick={handleVerify}
                        loading={verifying}
                        disabled={code.join("").length < 6}
                        className="otp-verify-btn w-full mb-4"
                      >
                        Verify
                      </Button>

                      <div className="flex items-center justify-between">
                        <Button
                          type="link"
                          onClick={() => {
                            setStep("email");
                            setCode(["", "", "", "", "", ""]);
                            setCodeError("");
                          }}
                          className="text-gray-400 text-xs p-0"
                        >
                          Use a different email
                        </Button>
                        <div>
                          <span className="text-gray-400 text-sm">Didn&apos;t receive it?&nbsp;</span>
                          <Button type="link" onClick={handleResend} loading={resending} className="text-blue-400 p-0 text-sm">
                            Resend
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Step 3: New Password */}
                  {step === "reset" && (
                    <Form layout="vertical" form={resetForm} onFinish={handleResetSubmit}>
                      <Form.Item
                        name="password"
                        label={<span className="text-gray-400">Password</span>}
                        rules={[{ required: true, message: "Please enter your password!" }]}
                      >
                        <Input.Password
                          placeholder="Enter your new password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="signup-input"
                        />
                      </Form.Item>

                      <Form.Item
                        name="confirmPassword"
                        label={<span className="text-gray-400">Confirm Password</span>}
                        dependencies={["password"]}
                        rules={[
                          { required: true, message: "Please confirm your password!" },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (!value || getFieldValue("password") === value) return Promise.resolve();
                              return Promise.reject(new Error("Passwords do not match!"));
                            },
                          }),
                        ]}
                      >
                        <Input.Password placeholder="Confirm your new password" className="signup-input" />
                      </Form.Item>

                      <div className="text-sm mb-4">
                        {[
                          [passwordCriteria.minLength, "At least 8 characters"],
                          [passwordCriteria.hasUppercase, "At least 1 uppercase letter"],
                          [passwordCriteria.hasLowercase, "At least 1 lowercase letter"],
                          [passwordCriteria.hasNumber, "At least 1 number"],
                          [passwordCriteria.hasSpecialChar, "At least 1 special character"],
                        ].map(([met, label]) => (
                          <p key={label} className="flex items-center">
                            {renderIcon(met)}
                            <span className={`ml-2 ${met ? "text-green-500" : "text-gray-500"}`}>{label}</span>
                          </p>
                        ))}
                      </div>

                      <div className="flex justify-end">
                        <Form.Item className="m-0">
                          <Button type="primary" htmlType="submit" loading={resetting} className="signup-button">
                            Reset Password
                          </Button>
                        </Form.Item>
                      </div>
                    </Form>
                  )}
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
